import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useApi } from "@core/api";
import { registerExtension } from "@core/components/PipelineComponents/ExtensionProvider";
import { Button } from "@core/components/ui/button";
import { Surface } from "@core/components/ui/surface";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTruckFast, faSpinner, faRotate } from "@fortawesome/pro-light-svg-icons";
import type { Fulfillment } from "@/apidtos";

interface Props {
  fulfillment: Fulfillment;
}

const FedExLabelButton = ({ fulfillment }: Props) => {
  const api = useApi();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [combine, setCombine] = useState<boolean | undefined>(undefined);
  const [selectedService, setSelectedService] = useState<string | undefined>(undefined);
  const [selectedPackaging, setSelectedPackaging] = useState<string | undefined>(undefined);
  const [fileIds, setFileIds] = useState<number[] | null>(null);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  const preview = useQuery({
    queryKey: ["fedex.preview.fulfillment", fulfillment.Id, combine],
    queryFn: () => api.PreviewFedExLabelPackagesForFulfillment(fulfillment.Id, combine),
    enabled: open,
  });

  const rates = useQuery({
    queryKey: ["fedex.rates.fulfillment", fulfillment.Id, combine],
    queryFn: () => api.GetFedExRatesForFulfillment(fulfillment.Id, combine),
    enabled: open && (preview.data?.IsSuccess ?? false),
    staleTime: 60 * 1000,
  });

  const generate = useMutation({
    mutationFn: () =>
      api.GenerateFedExLabelForFulfillment({
        FulfillmentId: fulfillment.Id,
        ServiceType: selectedService,
        PackagingType: selectedPackaging,
        CombinePackagesIntoOne: combine,
      }),
    onSuccess: (result) => {
      if (result?.IsSuccess && result.Result) {
        setFileIds(result.Result.FileIds);
        setErrorMessages([]);
      } else {
        setFileIds([]);
        setErrorMessages(result?.Messages ?? [t("ui.fedex.labelFailed", "Label generation failed")]);
      }
    },
    onError: (err: unknown) => {
      setErrorMessages([err instanceof Error ? err.message : String(err)]);
    },
  });

  const persistedFiles = (fulfillment.Files ?? [])
    .map((ff) => ff.File)
    .filter((f): f is NonNullable<typeof f> => !!f);
  const persistedIds = new Set(persistedFiles.map((f) => f.Id));

  if (!open) {
    return (
      <Surface className="p-4 my-2 space-y-3">
        <Button type="button" onClick={() => setOpen(true)}>
          <FontAwesomeIcon icon={faTruckFast} className="mr-2" />
          {t("ui.fedex.generateLabel", "Generate FedEx Label")}
        </Button>
        {persistedFiles.length > 0 && (
          <div>
            <h4 className="font-semibold text-sm">{t("ui.fedex.labels", "Generated Labels")}</h4>
            <ul className="list-disc ml-6">
              {persistedFiles.map((f) => (
                <li key={f.Id}>
                  <a className="text-blue-600 underline" href={`/api/file/${f.Id}`} download>
                    {f.FileName ?? `${t("ui.fedex.downloadLabel", "Download label")} #${f.Id}`}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Surface>
    );
  }

  const packages = preview.data?.Result ?? [];
  const previewError = preview.data && !preview.data.IsSuccess ? preview.data.Messages?.[0] : null;
  const rateQuotes = rates.data?.Result ?? [];
  const ratesError = rates.data && !rates.data.IsSuccess ? rates.data.Messages : null;
  const newFileIds = (fileIds ?? []).filter((id) => !persistedIds.has(id));
  const currencyFmt = (amount: number, currency: string) => {
    try {
      return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amount);
    } catch {
      return `${amount.toFixed(2)} ${currency}`;
    }
  };

  return (
    <Surface className="p-4 my-2 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">
          <FontAwesomeIcon icon={faTruckFast} className="mr-2" />
          {t("ui.fedex.generateLabel", "Generate FedEx Label")}
        </h3>
        <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
          {t("ui.close", "Close")}
        </Button>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold">{t("ui.fedex.packages", "Packages")}</h4>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={combine ?? false}
              onChange={(e) => setCombine(e.target.checked ? true : undefined)}
            />
            {t("ui.fedex.combinePackages", "Combine into one package")}
          </label>
        </div>
        {preview.isLoading && (
          <div className="text-sm text-gray-500">
            <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
            {t("ui.loading", "Loading...")}
          </div>
        )}
        {previewError && <div className="text-sm text-red-600">{previewError}</div>}
        {packages.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left">
                <th>#</th>
                <th>{t("ui.width", "Width")}</th>
                <th>{t("ui.depth", "Depth")}</th>
                <th>{t("ui.height", "Height")}</th>
                <th>{t("ui.weight", "Weight")}</th>
              </tr>
            </thead>
            <tbody>
              {packages.map((p, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td>{p.Width} {p.WidthUnitOfMeasure}</td>
                  <td>{p.Depth} {p.DepthUnitOfMeasure}</td>
                  <td>{p.Height} {p.HeightUnitOfMeasure}</td>
                  <td>{p.Weight} {p.WeightUnitOfMeasure}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {packages.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold">{t("ui.fedex.rateQuotes", "Rate Quotes")}</h4>
            <Button
              type="button"
              variant="ghost"
              onClick={() => rates.refetch()}
              disabled={rates.isFetching}
            >
              <FontAwesomeIcon icon={rates.isFetching ? faSpinner : faRotate} spin={rates.isFetching} className="mr-1" />
              {t("ui.refresh", "Refresh")}
            </Button>
          </div>
          {rates.isLoading && (
            <div className="text-sm text-gray-500">
              <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
              {t("ui.fedex.loadingRates", "Fetching FedEx rates...")}
            </div>
          )}
          {ratesError && ratesError.length > 0 && (
            <div className="p-3 border border-red-300 bg-red-50 text-red-700 text-sm rounded">
              <ul className="list-disc ml-6 space-y-1">
                {ratesError.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            </div>
          )}
          {rateQuotes.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left">
                  <th></th>
                  <th>{t("ui.fedex.service", "Service")}</th>
                  <th>{t("ui.fedex.packaging", "Packaging")}</th>
                  <th>{t("ui.fedex.delivery", "Est. Delivery")}</th>
                  <th className="text-right">{t("ui.fedex.price", "Price")}</th>
                </tr>
              </thead>
              <tbody>
                {rateQuotes.map((q) => {
                  const isSelected = selectedService === q.ServiceType;
                  return (
                    <tr
                      key={q.ServiceType}
                      className={`cursor-pointer hover:bg-gray-50 ${isSelected ? "bg-blue-50" : ""}`}
                      onClick={() => {
                        setSelectedService(q.ServiceType);
                        setSelectedPackaging(q.PackagingType);
                      }}
                    >
                      <td>
                        <input
                          type="radio"
                          name="fedex-rate"
                          checked={isSelected}
                          onChange={() => {
                            setSelectedService(q.ServiceType);
                            setSelectedPackaging(q.PackagingType);
                          }}
                        />
                      </td>
                      <td>
                        <div className="font-medium">{q.ServiceName}</div>
                        <div className="text-xs text-gray-500">{q.ServiceType}</div>
                      </td>
                      <td>{q.PackagingType}</td>
                      <td>{q.DeliveryDate ? new Date(q.DeliveryDate).toLocaleDateString() : "—"}</td>
                      <td className="text-right font-medium">{currencyFmt(q.TotalNetCharge, q.Currency)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      <Button
        type="button"
        onClick={() => generate.mutate()}
        disabled={generate.isPending || packages.length === 0 || !selectedService}
      >
        {generate.isPending
          ? <><FontAwesomeIcon icon={faSpinner} spin className="mr-2" />{t("ui.fedex.generating", "Generating...")}</>
          : selectedService
            ? t("ui.fedex.submitSelected", "Generate Label ({{service}})", { service: selectedService })
            : t("ui.fedex.selectRate", "Select a rate above to continue")}
      </Button>

      {errorMessages.length > 0 && (
        <div className="p-3 border border-red-300 bg-red-50 text-red-700 text-sm rounded">
          <div className="font-semibold mb-1">{t("ui.fedex.errors", "FedEx returned an error:")}</div>
          <ul className="list-disc ml-6 space-y-1">
            {errorMessages.map((m, i) => <li key={i}>{m}</li>)}
          </ul>
        </div>
      )}

      {(persistedFiles.length > 0 || newFileIds.length > 0) && (
        <div className="pt-2 border-t">
          <h4 className="font-semibold">{t("ui.fedex.labels", "Generated Labels")}</h4>
          <ul className="list-disc ml-6">
            {persistedFiles.map((f) => (
              <li key={f.Id}>
                <a className="text-blue-600 underline" href={`/api/file/${f.Id}`} download>
                  {f.FileName ?? `${t("ui.fedex.downloadLabel", "Download label")} #${f.Id}`}
                </a>
              </li>
            ))}
            {newFileIds.map((id) => (
              <li key={id}>
                <a className="text-blue-600 underline" href={`/api/file/${id}`} download>
                  {t("ui.fedex.downloadLabel", "Download label")} #{id}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Surface>
  );
};

registerExtension("admin.sales.fulfillment.tools", FedExLabelButton, "FedExLabelButton");
